import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameRoomService, type GameRoom } from '../../services/gameRoomService';
import QuickActions from '../../components/dashboard/QuickActions';
import DashboardPager from '../../components/dashboard/DashboardPager';
import '../Dashboard/Dashboard.css';
import './Rooms.css';

const RoomsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<GameRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRooms = async (isRefresh = false) => {
    try {
      setError(null);
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const response = await gameRoomService.getAvailableRooms();
      if (response && response.success && response.data) {
        const roomsData = (response.data as any).rooms || response.data;
        setRooms(Array.isArray(roomsData) ? roomsData : []);
      } else if (response && !response.success) {
        setRooms([]);
        setError(response.message || 'Failed to fetch rooms');
      } else {
        setRooms([]);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setError('Unable to load rooms — check your connection or login status.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRooms();
    const interval = setInterval(() => fetchRooms(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const handleJoinRoom = async (roomId: string | undefined) => {
    if (!roomId) return;
    try {
      const response = await gameRoomService.joinRoom(roomId);
      if (response.success) {
        navigate(`/game/${roomId}`);
      }
    } catch (error) {
      console.error('Error joining room:', error);
    }
  };

  const handleCreateRoom = () => {
    navigate('/rooms/create');
  };

  const availableRooms = rooms.filter(room => 
    room.status === 'waiting' && 
    Array.isArray(room.players) && 
    room.players.length < room.maxPlayers
  );

  return (
    <div className="modern-dashboard">
      <main className="dashboard-content">
        <DashboardPager autoIntervalMs={7000} pauseOnInteractionMs={60000}>
          <div>
            <section className="welcome-section">
              <h1 className="welcome-greeting">Game Rooms</h1>
              <p className="welcome-subtitle">Join an existing room or create your own to start playing.</p>
            </section>

            {error && (
              <div className="error-banner">
                <div className="error-message">{error}</div>
                <div className="error-actions">
                  <button onClick={() => fetchRooms(true)} className="btn-error-action">
                    Retry
                  </button>
                  <button onClick={() => navigate('/login')} className="btn-error-action">
                    Login
                  </button>
                </div>
              </div>
            )}

            <section className="actions-section">
              <QuickActions />
            </section>
          </div>

          <div>
            <section className="welcome-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div>
                  <h1 className="welcome-greeting">Available Rooms</h1>
                  <p className="welcome-subtitle">Join a room to start playing</p>
                </div>
                <button 
                  onClick={() => fetchRooms(true)}
                  disabled={refreshing}
                  className="btn-primary"
                  style={{ 
                    minWidth: '120px',
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.9rem'
                  }}
                >
                  {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
                </button>
              </div>
            </section>

            {loading ? (
              <div className="loading-state">
                <div className="loading-text">Loading rooms...</div>
              </div>
            ) : availableRooms.length === 0 ? (
              <section className="stats-section">
                <div className="stats-grid" style={{ justifyContent: 'center' }}>
                  <div className="empty-room-card">
                    <div className="empty-icon">🎮</div>
                    <h3 className="empty-title">No rooms available</h3>
                    <p className="empty-message">Be the first to create a room!</p>
                    <button onClick={handleCreateRoom} className="btn-create">
                      ➕ Create Room
                    </button>
                  </div>
                </div>
              </section>
            ) : (
              <section className="stats-section">
                <div className="stats-grid">
                  {availableRooms.slice(0, 4).map((room) => {
                    const playerCount = Array.isArray(room.players) ? room.players.length : 0;
                    return (
                      <div key={room.roomId} className="room-card-modern">
                        <div className="room-card-header">
                          <h3 className="room-name">{room.name || 'Game Room'}</h3>
                          <span className="room-game-type">
                            {room.gameType === 'kadi-ni-tidi' ? 'Kadi ni Tidi' : room.gameType}
                          </span>
                        </div>
                        <div className="room-card-body">
                          <div className="room-player-count">
                            <span className="count-number">{playerCount}/{room.maxPlayers}</span>
                            <span className="count-label">Players</span>
                          </div>
                          {Array.isArray(room.players) && room.players.length > 0 && (
                            <div className="room-players-list">
                              {room.players.slice(0, 2).map((player: any, idx: number) => (
                                <div key={idx} className="player-badge">
                                  <span>{player.isAI ? '🤖' : '👤'}</span>
                                  <span>{player.playerName}</span>
                                </div>
                              ))}
                              {room.players.length > 2 && (
                                <span className="more-players">+{room.players.length - 2} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="room-card-footer">
                          <div className="room-status">
                            <span className={`status-dot ${room.status}`}></span>
                            <span>{room.status}</span>
                          </div>
                          {room.status === 'waiting' && playerCount < room.maxPlayers && (
                            <button
                              onClick={() => handleJoinRoom(room.roomId)}
                              className="btn-join"
                            >
                              Join Room
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </DashboardPager>
      </main>
    </div>
  );
};

export default RoomsPage;
